import { Badge, Button, DataTable, EmptyState, Input, type Column } from '@axioma/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileSearch, Plus, RefreshCw, Scale, Search } from 'lucide-react';
import { useState } from 'react';

import { AutosViewer } from '../components/AutosViewer.js';
import { ImportProcessModal } from '../components/ImportProcessModal.js';
import { OabSetupModal } from '../components/OabSetupModal.js';
import { PageHeader } from '../components/PageHeader.js';
import { api, extractErrorMessage } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface ProcessRow {
  id: string;
  cnjNumberMasked: string;
  court: string;
  area: string;
  primarySubject: string | null;
  fazendaPublica: boolean;
  trackingActive: boolean;
  updatedAt: string;
}

function buildColumns(
  onOpenAutos: (cnj: string, masked: string) => void,
): ReadonlyArray<Column<ProcessRow>> {
  return [
    { key: 'cnj', header: 'CNJ', mono: true, render: (r) => r.cnjNumberMasked },
    { key: 'court', header: 'Tribunal', render: (r) => r.court },
    { key: 'area', header: 'Área', render: (r) => r.area },
    {
      key: 'subject',
      header: 'Assunto',
      render: (r) => r.primarySubject ?? '—',
      hiddenOnCompact: true,
    },
    {
      key: 'tracking',
      header: 'Monitoramento',
      render: (r) => (
        <span
          className={`font-mono text-[11px] uppercase tracking-wider ${r.trackingActive ? 'text-ax-electric' : 'text-ax-text-tertiary'}`}
        >
          {r.trackingActive ? '● Ativo' : '○ Inativo'}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Atualizado',
      mono: true,
      render: (r) => new Date(r.updatedAt).toLocaleDateString('pt-BR'),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<FileSearch size={12} />}
          onClick={(e) => {
            e.stopPropagation();
            onOpenAutos(r.cnjNumberMasked.replace(/\D/g, ''), r.cnjNumberMasked);
          }}
        >
          Autos
        </Button>
      ),
    },
  ];
}

export function Carteira(): JSX.Element {
  const tenant = useAuthStore((s) => s.currentTenantId);
  const user = useAuthStore((s) => s.user);
  const [search, setSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [oabOpen, setOabOpen] = useState(false);
  const [autosOpen, setAutosOpen] = useState<{ cnj: string; cnjMasked: string } | null>(null);
  const columns = buildColumns((cnj, cnjMasked) => setAutosOpen({ cnj, cnjMasked }));

  const oabConfigured = Boolean(user?.oabNumber && user?.oabState);

  const query = useQuery({
    queryKey: ['vigilia', 'processes', tenant, search],
    queryFn: async () => {
      const { data } = await api.get<{ items: ProcessRow[]; total: number }>('/vigilia/processes', {
        params: { pageSize: 100, search: search || undefined },
      });
      return data;
    },
    enabled: Boolean(tenant),
  });

  const qc = useQueryClient();
  const djenSync = useMutation({
    mutationFn: async () => {
      const desde = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const { data } = await api.post<{
        configured: boolean;
        comunicacoesEncontradas?: number;
        movementsCreated?: number;
        comunicacoesDeProcessosAindaNaoCadastrados?: number;
        error?: string;
      }>('/djen/sync', { desde });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vigilia', 'processes'] });
    },
  });

  return (
    <div>
      <PageHeader
        eyebrow="Vigília"
        title="Carteira processual"
        description="Processos sob monitoramento. Importe pelo CNJ via DataJud · DJEN captura intimações pela sua OAB."
        actions={
          <>
            {oabConfigured ? (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RefreshCw size={14} />}
                loading={djenSync.isPending}
                onClick={() => djenSync.mutate()}
                title={`Sincronizar DJEN (OAB ${user?.oabNumber}/${user?.oabState ?? ''})`}
              >
                Sync DJEN
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Scale size={14} />}
                onClick={() => setOabOpen(true)}
              >
                Cadastrar OAB
              </Button>
            )}
            <Button
              variant="signature"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setImportOpen(true)}
            >
              Importar processo (CNJ)
            </Button>
          </>
        }
      />
      <div className="space-y-4 px-8 py-6">
        {!oabConfigured && (
          <div className="flex items-start gap-3 rounded-ax-md border border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.05)] px-4 py-3">
            <Scale size={16} className="mt-0.5 flex-shrink-0 text-ax-aurora" />
            <div className="flex-1">
              <p className="font-display text-sm text-ax-text-primary">
                Cadastre sua OAB para ativar o DJEN
              </p>
              <p className="ax-body-sm mt-0.5 text-ax-text-secondary">
                A AXIOMA buscará automaticamente todas as intimações publicadas no Diário de Justiça
                Eletrônico Nacional em seu nome.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setOabOpen(true)}>
              Configurar
            </Button>
          </div>
        )}

        {djenSync.data && djenSync.data.configured && (
          <div className="rounded-ax-md border border-ax-electric bg-[rgba(72,150,255,0.05)] px-4 py-3">
            <p className="ax-body-sm text-ax-text-secondary">
              <strong className="text-ax-text-primary">DJEN sincronizado.</strong>{' '}
              {djenSync.data.comunicacoesEncontradas} comunicações no intervalo ·{' '}
              <Badge variant="aurora" uppercase>
                +{djenSync.data.movementsCreated} movimentos
              </Badge>
              {djenSync.data.comunicacoesDeProcessosAindaNaoCadastrados ? (
                <>
                  {' '}
                  · {djenSync.data.comunicacoesDeProcessosAindaNaoCadastrados} comunicações de
                  processos ainda não cadastrados (importe pelo CNJ)
                </>
              ) : null}
            </p>
          </div>
        )}

        {djenSync.isError && (
          <div className="rounded-ax-md border border-ax-critical bg-[rgba(255,82,82,0.05)] px-4 py-3 text-sm text-ax-critical">
            {extractErrorMessage(djenSync.error)}
          </div>
        )}

        <div className="max-w-md">
          <Input
            placeholder="Buscar por CNJ ou assunto..."
            leftAdornment={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {!query.isLoading && (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Carteira vazia"
            description="Importe o primeiro processo pelo número CNJ. Em segundos o DataJud entrega a estrutura completa e os movimentos passados."
            action={
              <Button
                variant="signature"
                leftIcon={<Download size={14} />}
                onClick={() => setImportOpen(true)}
              >
                Importar processo (CNJ)
              </Button>
            }
          />
        ) : (
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            rowKey={(r) => r.id}
            loading={query.isLoading}
          />
        )}
      </div>

      {importOpen && <ImportProcessModal onClose={() => setImportOpen(false)} />}
      {oabOpen && (
        <OabSetupModal
          initialNumber={user?.oabNumber}
          initialState={user?.oabState}
          onClose={() => setOabOpen(false)}
        />
      )}
      {autosOpen && (
        <AutosViewer
          cnj={autosOpen.cnj}
          cnjMasked={autosOpen.cnjMasked}
          onClose={() => setAutosOpen(null)}
        />
      )}
    </div>
  );
}
